<template>
    <div class="content-wrapper-scroll">
        <div class="content-wrapper">
            <div class="row">
                <div class="col-sm-12">
                    <div class="card">
                        <div class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <h5 class="mb-0">
                                Downloaded Reports
                            </h5>
                            <router-link to="/sap_equipments" class="btn btn-secondary btn-sm text-nowrap">
                                <i class="fas fa-arrow-left me-1"></i>
                                Back
                            </router-link>
                        </div>

                        <div class="card-body">
                            <div class="row mb-3">
                                <div class="col-sm-12">
                                    <div class="input-group">
                                        <input
                                            class="form-control"
                                            type="text"
                                            placeholder="Search by report name or file name"
                                            v-model="meta.search"
                                            @keyup.enter="search"
                                        />
                                        <span class="input-group-text">
                                            <i class="fas fa-search"></i>
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div class="table-responsive tabletop">
                                <table class="table table-bordered table-striped">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th @click="sort('report_name')" class="cursor-pointer">
                                                Report Name
                                                <i :class="sortIcon('report_name')"></i>
                                            </th>
                                            <th @click="sort('file_name')" class="cursor-pointer">
                                                File Name
                                                <i :class="sortIcon('file_name')"></i>
                                            </th>
                                            <th>Generated By</th>
                                            <th @click="sort('date_time')" class="cursor-pointer">
                                                Generated At
                                                <i :class="sortIcon('date_time')"></i>
                                            </th>
                                            <th class="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-if="reports.length === 0">
                                            <td colspan="6" class="text-center py-4">No downloaded reports found</td>
                                        </tr>
                                        <tr
                                            v-for="(report, key) in reports"
                                            :key="report.downloaded_report_id"
                                        >
                                            <td>{{ meta.from + key }}</td>
                                            <td>{{ report.report_name }}</td>
                                            <td>{{ report.file_name }}</td>
                                            <td>{{ report.user_name || '—' }}</td>
                                            <td>{{ formatDateTime(report.date_time) }}</td>
                                            <td class="text-center text-nowrap">
                                                <button type="button" class="btn btn-success btn-sm me-1" title="Download report" @click="downloadReport(report)">
                                                    <i class="fas fa-download"></i>
                                                </button>
                                                <button type="button" class="btn btn-danger btn-sm" title="Delete report" @click="deleteReport(report)">
                                                    <i class="fas fa-trash"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div class="form-actions-footer">
                                <div class="row align-items-center">
                                    <div class="col-sm-2">
                                        <select class="form-control" v-model="meta.per_page" @change="onPerPageChange">
                                            <option v-for="n in [10, 15, 20, 25, 30]" :key="n">{{ n }}</option>
                                        </select>
                                    </div>
                                    <div class="col-sm-10 text-end">
                                        <Pagination :maxPage="meta.maxPage" :totalPages="meta.lastPage" :currentPage="meta.currentPage" @pagechanged="onPageChange"/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Pagination from "@/components/Pagination.vue";
    import axios from "axios";
    import moment from "moment";

    export default {
        components: { Pagination },

        computed: {
            plantLabel() {
                const user = this.$store.getters.user;
                if (!user) return "";
                if (user.plant && user.plant.plant_name) return user.plant.plant_name;
                return user.plant_id ? "Plant #" + user.plant_id : "";
            },
        },

        data() {
            return {
                reports: [],
                meta: {
                    search: "",
                    order_by: "desc",
                    field: "date_time",
                    per_page: 10,
                    currentPage: 1,
                    lastPage: 1,
                    from: 1,
                    maxPage: 1,
                    plant_id: this.$store.getters.user && this.$store.getters.user.plant_id,
                },
            };
        },

        mounted() {
            const user = this.$store.getters.user;
            if (user && user.plant_id) {
                this.meta.plant_id = user.plant_id;
            }
            this.index();
        },

        methods: {
            index() {
                const vm = this;
                if (!vm.meta.plant_id) {
                    vm.$store.dispatch("error", "Plant is required to load downloaded reports.");
                    return;
                }

                const loader = vm.$loading.show();
                const uri = "paginateDownloadedReports?page=" + vm.meta.currentPage;
                vm.$store.dispatch("post", {uri,
                    data: {plant_id: vm.meta.plant_id, search: vm.meta.search || "", order_by: vm.meta.order_by, field: vm.meta.field, per_page: vm.meta.per_page,},
                }).then((res) => {
                    vm.reports = res.data.data;
                    vm.meta.lastPage = res.data.meta.last_page;
                    vm.meta.from = res.data.meta.from;
                    vm.meta.maxPage = vm.meta.lastPage >= 3 ? 3 : vm.meta.lastPage;
                }).catch((error) => {
                    const msg =
                        (error.response && error.response.data && error.response.data.message) ||
                        "Could not load downloaded reports.";
                    vm.$store.dispatch("error", msg);
                }).finally(() => loader.hide());
            },

            search() {
                this.meta.currentPage = 1;
                this.index();
            },

            onPageChange(page) {
                this.meta.currentPage = page;
                this.index();
            },

            onPerPageChange() {
                this.meta.currentPage = 1;
                this.index();
            },

            sort(field) {
                if (this.meta.field === field) {
                    this.meta.order_by = this.meta.order_by === "asc" ? "desc" : "asc";
                } else {
                    this.meta.field = field;
                    this.meta.order_by = "asc";
                }
                this.index();
            },

            sortIcon(field) {
                if (this.meta.field !== field) return "fas fa-sort";
                return this.meta.order_by === "asc" ? "fas fa-sort-up" : "fas fa-sort-down";
            },

            formatDateTime(value) {
                if (!value) return "";
                return moment(value).format("DD MMM YYYY, HH:mm");
            },

            downloadReport(report) {
                const vm = this;
                const user = vm.$store.getters.user;

                if (!user || !user.token) {
                    vm.$store.dispatch("error", "You must be logged in to download this report.");
                    return;
                }

                const loader = vm.$loading.show();
                const url = vm.$store.getters.apiUrl + "downloadDownloadedReport/" + report.downloaded_report_id + "?plant_id=" + vm.meta.plant_id;

                axios.get(url, {
                        headers: { Authorization: "Bearer " + user.token },
                        responseType: "blob",
                    }).then(function (response) {
                        const fileName = (report.file_name || "").toLowerCase();
                        let mimeType = "application/pdf";

                        if (fileName.endsWith(".zip")) {
                            mimeType = "application/zip";
                        } else if (fileName.endsWith(".xlsx")) {
                            mimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
                        }

                        const blob = new Blob([response.data], {
                            type: mimeType,
                        });
                        const dlUrl = window.URL.createObjectURL(blob);
                        const link = document.createElement("a");
                        link.href = dlUrl;
                        link.setAttribute("download", report.file_name);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(dlUrl);
                    }).catch(function () {
                        vm.$store.dispatch("error", "Could not download the report file.");
                    }).finally(function () {
                        loader.hide();
                    });
            },

            deleteReport(report) {
                const vm = this;
                if (!confirm("Delete this report? The file will be removed permanently.")) {
                    return;
                }

                const loader = vm.$loading.show();
                vm.$store.dispatch("post", {uri: "deleteDownloadedReport/" + report.downloaded_report_id,
                data: {
                    plant_id: vm.meta.plant_id,
                },}).then((res) => {
                    const msg = (res.data && res.data.message) || "Downloaded report deleted successfully.";
                    vm.$store.dispatch("success", msg);
                    vm.index();
                }).catch((error) => {
                    const msg =(error.response && error.response.data && error.response.data.message) || "Could not delete the report."; 
                    vm.$store.dispatch("error", msg);
                }).finally(() => loader.hide());
            },
        },
    };
</script>

<style scoped>
    .cursor-pointer {
        cursor: pointer;
    }
</style>
